import UIFlex from 'UIComponents/layout/UIFlex';
import { useCallback, useEffect, useState } from 'react';
import { updateAction } from 'automation-ui-canvas/flow/utils/updateAction';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import { defaultErrorHandlingForUpdateFlow, useUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { getSourceApp } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { useDispatch } from 'react-redux';
import GoToActionConfirmationPopoverContent from 'automation-ui-canvas/containers/flow-views/go-to-action/GoToActionConfirmationPopoverContent';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import Action from 'automation-ui-action-features/records/Action';
import GoToActionCardWrapper from 'automation-ui-canvas/action-features/go-to/GoToActionCardWrapper';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DETAIL_MIN_ZOOM_LEVEL } from 'automation-ui-flow-core/canvas/graphConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const GoToActionFlowViewContent = ({
  viewData,
  Canvas,
  flow,
  onCancel
}) => {
  const [updateFlow] = useUpdateFlow();
  const [stagedFlow, setStagedFlow] = useState(flow);
  const [isSaving, setIsSaving] = useState(false);
  const sourceApp = getSourceApp(flow);
  const dispatch = useDispatch();
  const {
    actionCardPropsById,
    prevActionId,
    prevAction,
    getActionIdsForGoToAction,
    edgeId,
    updateActionCardProps,
    onPopoverCancel,
    isNewAction,
    currentActionId,
    correctedEdgeId
  } = viewData;
  if (!edgeId) {
    onCancel();
  }
  const keyDownEventListener = useCallback(event => {
    if (event.key === 'Escape') {
      onCancel();
    }
  }, [onCancel]);
  useEffect(() => {
    document.addEventListener('keydown', keyDownEventListener);
    if (!flow || !isNewAction) {
      return;
    }
    const emptyAction = new Action({
      actionType: ActionTypes.EMPTY_ACTION,
      actionId: SpecialActionIds.NEW_ACTION
    });
    let newFlow = stagedFlow;
    newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
    newFlow = newFlow.insertAction({
      action: emptyAction,
      edgeId,
      actionId: SpecialActionIds.NEW_ACTION
    });
    updateActionCardProps(currentActionId, {
      borderStyle: 'selected'
    });
    setStagedFlow(newFlow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', keyDownEventListener);
    };
  }, [keyDownEventListener]);
  const handleOnMouseEnter = useCallback(id => {
    if (!flow || !edgeId || !prevActionId || isSaving) {
      return;
    }
    const flowAsGraph = translateToGraph(flow, true);
    const actionIds = getActionIdsForGoToAction(flowAsGraph)(prevActionId);
    const canGoToAction = actionIds.includes(id);
    if (!canGoToAction) {
      return;
    }
    const newFlow = flow.removeAction(SpecialActionIds.NEW_ACTION);
    let newAction = newFlow.getActionById(prevAction);
    if (!isNewAction) {
      newAction = newAction.removeEdgeFromConnection(correctedEdgeId !== null && correctedEdgeId !== void 0 ? correctedEdgeId : edgeId);
    }
    newAction = newAction.addEdgeToConnection(correctedEdgeId !== null && correctedEdgeId !== void 0 ? correctedEdgeId : edgeId, id, true);
    setStagedFlow(updateAction({
      flow: newFlow,
      action: newAction,
      edgeId
    }));
  }, [flow, edgeId, prevAction, prevActionId, getActionIdsForGoToAction, isSaving, isNewAction, correctedEdgeId]);
  const onPopoverSave = useCallback(id => {
    updateActionCardProps(id, {
      renderConfirmationPopoverContent: null
    });
    setIsSaving(true);
    updateFlow({
      variables: {
        flow: stagedFlow,
        sourceApp: sourceApp
      }
    }).then(() => {
      onCancel();
    }).catch(error => {
      defaultErrorHandlingForUpdateFlow({
        error,
        dispatch
      });
    }).finally(() => {
      setIsSaving(false);
    });
  }, [updateActionCardProps, onCancel, updateFlow, stagedFlow, sourceApp, dispatch]);
  const handleOnSelectAction = useCallback(id => {
    if (!flow || !edgeId || !prevActionId) {
      return;
    }
    const flowAsGraph = translateToGraph(flow, true);
    const actionIds = getActionIdsForGoToAction(flowAsGraph)(prevActionId);
    const canGoToAction = actionIds.includes(id);
    if (!canGoToAction) {
      return;
    }
    actionIds.forEach(actionId => {
      if (actionId !== id) {
        updateActionCardProps(actionId, {
          renderConfirmationPopoverContent: null
        });
      }
    });
    updateActionCardProps(id, {
      renderConfirmationPopoverContent: () => /*#__PURE__*/_jsx(GoToActionConfirmationPopoverContent, {
        id: id,
        onSave: onPopoverSave,
        onCancel: onPopoverCancel
      })
    });
  }, [flow, edgeId, prevActionId, getActionIdsForGoToAction, updateActionCardProps, onPopoverCancel, onPopoverSave]);
  const newActionNode = options => {
    const {
      id,
      width,
      height,
      zoomLevel
    } = options;
    const actionCardProps = actionCardPropsById === null || actionCardPropsById === void 0 ? void 0 : actionCardPropsById.get(id);
    return /*#__PURE__*/_jsx(GoToActionCardWrapper, {
      flow: flow,
      actionId: id,
      isSelected: false,
      isHighlighted: !!(actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.borderStyle) || id === SpecialActionIds.NEW_ACTION,
      isDisabled: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.disabled,
      disabledDescription: actionCardProps !== null && actionCardProps !== void 0 && actionCardProps.disabledDescription ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: actionCardProps === null || actionCardProps === void 0 ? void 0 : actionCardProps.disabledDescription
      }) : undefined,
      width: width,
      height: height,
      layout: zoomLevel < DETAIL_MIN_ZOOM_LEVEL ? 'zoomed-out' : 'default'
    });
  };
  return /*#__PURE__*/_jsx(UIFlex, {
    direction: "row",
    children: /*#__PURE__*/_jsx(Canvas, {
      actionCardPropsById: actionCardPropsById,
      flow: stagedFlow,
      onMouseEnterNode: handleOnMouseEnter,
      onSelectAction: handleOnSelectAction,
      newActionNode: newActionNode
    })
  });
};
export default GoToActionFlowViewContent;